import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { NFT_CONTRACT_ADDRESS } from '../config/contracts';
import UnauthorizedPage from '../pages/UnauthorizedPage';
import ForbiddenPage from '../pages/ForbiddenPage';

const ADMIN_WALLET = (process.env.REACT_APP_ADMIN_WALLET_ADDRESS || '').toLowerCase();

// Hold render until both the on-chain owner check has settled AND the wallet
// / auth contexts have had a beat to restore from localStorage. Without the
// brief grace period, a full-page reload (e.g. clicking a link to /admin)
// can flash the 403 page for a frame before contexts hydrate.
const HYDRATION_GRACE_MS = 800;

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { account, isConnected, web3 } = useWeb3();
  const [isOnChainAdmin, setIsOnChainAdmin] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [graceElapsed, setGraceElapsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setGraceElapsed(true), HYDRATION_GRACE_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        if (!web3 || !account || !NFT_CONTRACT_ADDRESS) {
          if (!cancelled) { setIsOnChainAdmin(false); setOwnerChecked(true); }
          return;
        }
        const data = '0x8da5cb5b';
        const result = await web3.eth.call({ to: NFT_CONTRACT_ADDRESS, data });
        const owner = result ? `0x${String(result).slice(-40)}` : '';
        if (!cancelled) {
          setIsOnChainAdmin(!!owner && owner.toLowerCase() === account.toLowerCase());
          setOwnerChecked(true);
        }
      } catch {
        if (!cancelled) { setIsOnChainAdmin(false); setOwnerChecked(true); }
      }
    };
    check();
    return () => { cancelled = true; };
  }, [web3, account]);

  const walletLower = (account || '').toLowerCase();
  const isEnvAdmin = ADMIN_WALLET && walletLower === ADMIN_WALLET;
  const isDbAdmin = isAuthenticated && user?.isAdmin === true;
  const isAnyAdmin = isEnvAdmin || isDbAdmin || (isConnected && isOnChainAdmin);

  // While we're still hydrating contexts (and we haven't yet confirmed
  // admin), show a quiet placeholder rather than the 403 page — avoids the
  // flash on /admin reloads / alert clicks.
  if (!ownerChecked) return null;
  if (!graceElapsed && !isAnyAdmin) return null;

  if (!isAuthenticated && !isConnected) return <UnauthorizedPage />;
  if (!isAnyAdmin) return <ForbiddenPage />;

  return children;
};

export default AdminRoute;
